import { invokeFunction } from '../api'
import { toApiRequestError } from '../errors'

export interface TrainingModule {
  module_id: string
  title: string
  version_number: number
  status: 'draft' | 'approved' | 'retired'
  expiration_interval_days: number
  ai_generated: boolean
  module_code?: string | null
  package_version?: string | null
  package_version_key?: string | null
  duration_minutes?: number | null
  domain_tags?: string[]
  learning_depth?: string | null
  cadence_tags?: string[]
  role_applicability?: string[]
  related_track_ids?: string[]
  related_recommendation_types?: string[]
  library_origin?: string | null
  linked_policy_refs?: string[]
  source_markdown_path?: string | null
  trust_signal_actors?: {
    author?: { name?: string; credentials?: string; role?: string; status?: string } | null
    reviewer?: { name?: string; credentials?: string; role?: string; status?: string } | null
    approver?: { name?: string; credentials?: string; role?: string; status?: string } | null
  } | null
  clinical_accuracy_scope?: string | null
  last_reviewed_at?: string | null
  next_review_due_at?: string | null
  mapped_standard_ids?: string[]
  content_blocks?: Array<{ type?: string; text?: string }>
  created_at?: string
  updated_at?: string
}

export interface TrainingModuleCitation {
  module_citation_id: string
  source_type: string
  citation: string
  label: string
  required_for_defensibility: boolean
}

export interface TrainingModuleAsset {
  asset_id: string
  kind: 'slides' | 'audio' | 'video' | 'quiz' | 'attachment'
  status: 'draft' | 'review' | 'approved' | 'retired'
  storage_mode: 'object_storage_ref' | 'external_url'
  storage_ref: string
  mime_type: string
  generated_from: 'module_markdown' | 'module_manifest' | 'manual_upload'
  asset_version: string
  approved_at?: string | null
}

export interface AssetSignedUrl {
  asset_id: string
  module_id?: string
  kind?: string
  mode: 'object_storage_ref' | 'external_url'
  mime_type: string
  url: string
  expires_in?: number
}

export interface TrainingModuleOverlaySummary {
  overlay_id: string
  facility_id?: string | null
  overlay_scope: 'facility' | 'enterprise'
  status: 'draft' | 'approved' | 'retired'
  approved_at?: string | null
}

export interface TrainingTrack {
  track_id: string
  track_key: string
  title: string
  description?: string | null
  status: 'active' | 'archived'
  module_count: number
  deployment_count?: number
  last_deployed_at?: string | null
  created_at?: string
  updated_at?: string
}

export interface TrainingTrackDeployment {
  deployment_id: string
  track_id: string
  deployment_scope: 'facility' | 'role' | 'individual'
  facility_id?: string | null
  target_role?: string | null
  assignee_user_id?: string | null
  due_date: string
  deployment_status: 'pending' | 'completed' | 'failed'
  assignment_count: number
  deployment_notes?: string | null
  created_by?: string | null
  created_at: string
  updated_at?: string
  assignments?: TrainingAssignment[]
}

export interface TrainingTrackDetail extends TrainingTrack {
  modules: TrainingModule[]
  deployments: TrainingTrackDeployment[]
}

export interface TrainingModulePolicyLink {
  module_policy_link_id: string
  policy_id: string
  reference_label?: string | null
  policies?: {
    title?: string
    category?: string
    status?: string
    version_number?: number
  }
}

export interface TrainingModuleDetail extends TrainingModule {
  education_module_citations?: TrainingModuleCitation[]
  education_module_assets?: TrainingModuleAsset[]
  education_module_policy_links?: TrainingModulePolicyLink[]
  education_module_overlays?: TrainingModuleOverlaySummary[]
}

export interface TrainingReviewDecision {
  review_decision_id: string
  entity_type: 'module' | 'asset' | 'quiz'
  module_id?: string | null
  asset_id?: string | null
  quiz_id?: string | null
  decision: 'approved' | 'rejected'
  review_comment?: string | null
  reviewer_user_id?: string | null
  decision_context?: Record<string, unknown>
  created_at: string
}

export interface TrainingReviewerQueueItem {
  queue_key: string
  entity_type: 'module' | 'asset' | 'quiz'
  entity_id: string
  module_id: string
  title: string
  subtitle?: string | null
  status: string
  created_at: string
  updated_at?: string | null
  detail: {
    module?: TrainingModuleDetail
    asset?: TrainingModuleAsset & {
      module_id?: string
      created_at?: string
      updated_at?: string
      education_modules?: {
        title?: string
        version_number?: number
        module_code?: string | null
        status?: string
        mapped_standard_ids?: string[]
        trust_signal_actors?: TrainingModule['trust_signal_actors']
      }
    }
    quiz?: QuizReviewItem & {
      passing_score_percent?: number
      updated_at?: string
      education_modules?: {
        title?: string
        version_number?: number
        module_code?: string | null
      }
    }
  }
  latest_decision?: TrainingReviewDecision | null
  decision_history?: TrainingReviewDecision[]
}

export interface QuizReviewItem {
  quiz_id: string
  module_id: string
  status: 'draft' | 'approved'
  ai_generated: boolean
  created_at: string
  education_modules?: {
    title?: string
    version_number?: number
  }
}

export interface QuizQuestionView {
  question_id: string
  question_text: string
  answer_options: string[]
}

export interface QuizPlayerData {
  quiz: {
    quiz_id: string
    module_id: string
    status: 'draft' | 'approved'
    passing_score_percent: number
    education_modules?: {
      title?: string
      version_number?: number
    }
  }
  questions: QuizQuestionView[]
}

export interface QuizAttemptResult {
  score_percent: number
  passed: boolean
  correct_count: number
  question_count: number
  scoring_version: string
  attempted_at: string
}

export interface TrainingAssignment {
  assignment_id: string
  module_id: string
  assignment_scope: 'facility' | 'role' | 'individual'
  assignment_state?: 'assigned' | 'in_progress' | 'completed' | 'cancelled'
  facility_id?: string | null
  target_role?: string | null
  assignee_user_id?: string | null
  source_type?: 'manual' | 'recurring' | 'recommendation' | 'capa' | 'corporate'
  assigned_by_scope?: 'manual' | 'recommendation' | 'corporate' | 'system'
  recommendation_id?: string | null
  linked_capa_id?: string | null
  due_date: string
  created_at: string
  latest_completion?: {
    completion_id: string
    completed_at?: string | null
    expires_at?: string | null
    attestation_flag?: boolean | null
    completion_depth?: string | null
    evidence_package_id?: string | null
  } | null
  learner_asset_progress?: {
    started_count: number
    completed_count: number
    latest_started_at?: string | null
    latest_completed_at?: string | null
  } | null
  education_modules?: {
    title?: string
    version_number?: number
    module_code?: string | null
    learning_depth?: string | null
    duration_minutes?: number | null
    related_track_ids?: string[]
    linked_policy_refs?: string[]
    content_blocks?: Array<{ type?: string; text?: string }>
    education_module_assets?: TrainingModuleAsset[]
  }
}

export interface CompletionTranscriptRow {
  completion_id: string
  assignment_id: string
  user_id: string
  completed_at: string
  expires_at: string
  attestation_flag: boolean
  evidence_package_id?: string | null
  completion_depth?: string | null
  training_assignments?: {
    due_date?: string
    assignment_scope?: 'facility' | 'role' | 'individual'
    module_id?: string
    facility_id?: string | null
    target_role?: string | null
    assignee_user_id?: string | null
    source_type?: 'manual' | 'recurring' | 'recommendation' | 'capa' | 'corporate'
    source_reference_id?: string | null
    recommendation_id?: string | null
    linked_capa_id?: string | null
    education_modules?: {
      title?: string
      version_number?: number
      module_code?: string | null
      learning_depth?: string | null
    }
  }
  deployment_context?: {
    deployment_id: string
    track_id: string
    track_key?: string | null
    track_title?: string | null
    track_status?: string | null
    deployment_scope: 'facility' | 'role' | 'individual'
    deployment_status: 'pending' | 'completed' | 'failed'
    assignment_count: number
    due_date?: string | null
    created_at?: string | null
    facility_id?: string | null
    target_role?: string | null
    assignee_user_id?: string | null
  } | null
  recommendation_link?: {
    recommendation_link_id?: string | null
    recommendation_id: string
    module_id?: string | null
    linked_capa_id?: string | null
    remediation_strength: 'training_only' | 'training_plus_checklist' | 'training_plus_system_change'
    created_at?: string | null
  } | null
}

export interface RecommendationDraft {
  recommendation_id: string
  module_id: string
  standard_id: string
  recommendation_text: string
  status: 'draft' | 'approved'
  created_at: string
  education_modules?: {
    title?: string
    version_number?: number
  }
}

export interface TrainingEvidencePackage {
  evidence_package_id: string
  assignment_id: string
  completion_id: string
  module_id: string
  user_id: string
  package_status: 'draft' | 'generated' | 'approved' | 'exported'
  redacted: boolean
  payload: Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface TrainingRecommendationLink {
  recommendation_link_id: string
  recommendation_id: string
  module_id: string
  linked_capa_id?: string | null
  remediation_strength: 'training_only' | 'training_plus_checklist' | 'training_plus_system_change'
  created_at: string
}

export interface TrainingCompetencyValidation {
  competency_validation_id: string
  assignment_id: string
  module_id: string
  user_id: string
  validator_user_id?: string | null
  validation_status: 'pending' | 'approved' | 'rejected'
  validation_notes?: string | null
  validated_at?: string | null
  created_at: string
  updated_at: string
  history?: Array<{
    competency_validation_history_id: string
    event_type: 'requested' | 'approved' | 'rejected'
    event_notes?: string | null
    created_at: string
    actor_user_id?: string | null
    actor_ref?: string | null
  }>
  latest_history_entry?: {
    competency_validation_history_id: string
    event_type: 'requested' | 'approved' | 'rejected'
    event_notes?: string | null
    created_at: string
    actor_user_id?: string | null
    actor_ref?: string | null
  } | null
  latest_completion?: {
    completion_id: string
    completed_at?: string | null
    expires_at?: string | null
    attestation_flag?: boolean | null
    completion_depth?: string | null
    evidence_package_id?: string | null
  } | null
  evidence_package?: {
    evidence_package_id: string
    package_status: 'draft' | 'generated' | 'approved' | 'exported'
    redacted: boolean
    created_at: string
    updated_at: string
    completion_id?: string | null
  } | null
  training_assignments?: {
    assignment_id?: string
    due_date?: string
    assignment_scope?: 'facility' | 'role' | 'individual'
    education_modules?: {
      title?: string
      version_number?: number
      module_code?: string | null
      learning_depth?: string | null
    }
  }
}

function ensureArray<T>(value: unknown, fallback: T[] = []): T[] {
  return Array.isArray(value) ? (value as T[]) : fallback
}

function ensureContentBlocks(value: unknown): Array<{ type?: string; text?: string }> {
  if (!Array.isArray(value)) return []
  return value
    .map((block) => {
      if (typeof block === 'string') return { type: 'text', text: block }
      if (block && typeof block === 'object') return block as { type?: string; text?: string }
      return null
    })
    .filter((block): block is { type?: string; text?: string } => block !== null)
}

function normalizeTrainingAssignment(assignment: TrainingAssignment): TrainingAssignment {
  return {
    ...assignment,
    latest_completion: assignment.latest_completion ?? null,
    learner_asset_progress: assignment.learner_asset_progress ?? null,
    education_modules: assignment.education_modules
      ? {
        ...assignment.education_modules,
        related_track_ids: ensureArray<string>(assignment.education_modules.related_track_ids),
        linked_policy_refs: ensureArray<string>(assignment.education_modules.linked_policy_refs),
        content_blocks: ensureContentBlocks(assignment.education_modules.content_blocks),
        education_module_assets: ensureArray<TrainingModuleAsset>(assignment.education_modules.education_module_assets),
      }
      : undefined,
  }
}

export async function listTrainingModules(): Promise<TrainingModule[]> {
  const { data, error } = await invokeFunction<{ modules: TrainingModule[] }>('training', {
    method: 'GET',
    path: '/modules',
  })

  if (error) throw toApiRequestError(error, 'Failed to load training modules')
  return ensureArray<TrainingModule>(data?.modules).map((module) => ({
    ...module,
    mapped_standard_ids: ensureArray<string>(module.mapped_standard_ids),
    domain_tags: ensureArray<string>(module.domain_tags),
    cadence_tags: ensureArray<string>(module.cadence_tags),
    role_applicability: ensureArray<string>(module.role_applicability),
    related_track_ids: ensureArray<string>(module.related_track_ids),
    related_recommendation_types: ensureArray<string>(module.related_recommendation_types),
    linked_policy_refs: ensureArray<string>(module.linked_policy_refs),
    content_blocks: ensureContentBlocks(module.content_blocks),
  }))
}

export async function createTrainingModule(payload: {
  title: string
  module_code?: string
  content_blocks: unknown[]
  mapped_standard_ids: string[]
  expiration_interval_days: number
  duration_minutes?: number
  domain_tags?: string[]
  learning_depth?: string
  cadence_tags?: string[]
  role_applicability?: string[]
  related_track_ids?: string[]
  related_recommendation_types?: string[]
  linked_policy_refs?: string[]
  trust_signal_actors?: {
    author?: { name?: string; role?: string; status?: string }
    reviewer?: { name?: string; role?: string; status?: string }
    approver?: { name?: string; role?: string; status?: string }
  }
  clinical_accuracy_scope?: string
  last_reviewed_at?: string
  next_review_due_at?: string
}): Promise<TrainingModule> {
  const { data, error } = await invokeFunction<{ module: TrainingModule }>('training', {
    method: 'POST',
    path: '/modules',
    body: payload,
  })

  if (error || !data?.module) throw toApiRequestError(error, 'Failed to create module')
  return data.module
}

export async function getTrainingModuleDetail(moduleId: string): Promise<TrainingModuleDetail> {
  const { data, error } = await invokeFunction<{ module: TrainingModuleDetail }>('training', {
    method: 'GET',
    path: `/modules/${moduleId}`,
  })

  if (error || !data?.module) throw toApiRequestError(error, 'Failed to load training module detail')
  return {
    ...data.module,
    mapped_standard_ids: ensureArray<string>(data.module.mapped_standard_ids),
    content_blocks: ensureContentBlocks(data.module.content_blocks),
    domain_tags: ensureArray<string>(data.module.domain_tags),
    cadence_tags: ensureArray<string>(data.module.cadence_tags),
    role_applicability: ensureArray<string>(data.module.role_applicability),
    related_track_ids: ensureArray<string>(data.module.related_track_ids),
    related_recommendation_types: ensureArray<string>(data.module.related_recommendation_types),
    linked_policy_refs: ensureArray<string>(data.module.linked_policy_refs),
    education_module_citations: ensureArray<TrainingModuleCitation>(data.module.education_module_citations),
    education_module_assets: ensureArray<TrainingModuleAsset>(data.module.education_module_assets),
    education_module_policy_links: ensureArray<TrainingModulePolicyLink>(data.module.education_module_policy_links),
    education_module_overlays: ensureArray<TrainingModuleOverlaySummary>(data.module.education_module_overlays),
  }
}

/**
 * Resolves a governed training-module asset (education_module_assets row)
 * to a short-lived signed playback/download URL via the Hono data-plane
 * training-assets resolver (#1764 Phase 1). object_storage_ref assets get a
 * presigned URL against the training-media bucket; external_url assets pass
 * their stored ref straight through.
 */
export async function getAssetSignedUrl(assetId: string): Promise<AssetSignedUrl> {
  const { data, error } = await invokeFunction<AssetSignedUrl>('training-assets', {
    method: 'GET',
    path: `/${assetId}:download`,
  })

  if (error || !data?.url) throw toApiRequestError(error, 'Failed to resolve asset playback URL')
  return data
}

export async function listTrainingTracks(): Promise<TrainingTrack[]> {
  const { data, error } = await invokeFunction<{ tracks: TrainingTrack[] }>('training', {
    method: 'GET',
    path: '/tracks',
  })

  if (error) throw toApiRequestError(error, 'Failed to load training tracks')
  return ensureArray<TrainingTrack>(data?.tracks)
}

export async function getTrainingTrackDetail(trackId: string): Promise<TrainingTrackDetail> {
  const { data, error } = await invokeFunction<{ track: TrainingTrackDetail }>('training', {
    method: 'GET',
    path: `/tracks/${trackId}`,
  })

  if (error || !data?.track) throw toApiRequestError(error, 'Failed to load training track detail')
  return {
    ...data.track,
    modules: ensureArray<TrainingModule>(data.track.modules).map((module) => ({
      ...module,
      mapped_standard_ids: ensureArray<string>(module.mapped_standard_ids),
      domain_tags: ensureArray<string>(module.domain_tags),
      cadence_tags: ensureArray<string>(module.cadence_tags),
      role_applicability: ensureArray<string>(module.role_applicability),
      related_track_ids: ensureArray<string>(module.related_track_ids),
      related_recommendation_types: ensureArray<string>(module.related_recommendation_types),
      linked_policy_refs: ensureArray<string>(module.linked_policy_refs),
      content_blocks: ensureContentBlocks(module.content_blocks),
    })),
    deployments: ensureArray<TrainingTrackDeployment>(data.track.deployments).map((deployment) => ({
      ...deployment,
      assignments: ensureArray<TrainingAssignment>(deployment.assignments),
    })),
  }
}

export async function createTrainingTrackDeployment(trackId: string, payload: {
  deployment_scope: 'facility' | 'role' | 'individual'
  due_date: string
  facility_id?: string
  target_role?: string
  assignee_user_id?: string
  deployment_notes?: string
}): Promise<TrainingTrackDeployment> {
  const { data, error } = await invokeFunction<{ deployment: TrainingTrackDeployment }>('training', {
    method: 'POST',
    path: `/tracks/${trackId}/deployments`,
    body: payload,
  })

  if (error || !data?.deployment) throw toApiRequestError(error, 'Failed to deploy training track')
  return {
    ...data.deployment,
    assignments: ensureArray<TrainingAssignment>(data.deployment.assignments),
  }
}

export async function generateQuizDraft(moduleId: string, prompt: string): Promise<QuizReviewItem> {
  const { data, error } = await invokeFunction<{ quiz: QuizReviewItem }>('training', {
    method: 'POST',
    path: '/quizzes/generate-draft',
    body: { module_id: moduleId, prompt },
  })

  if (error || !data?.quiz) throw toApiRequestError(error, 'Failed to generate quiz draft')
  return data.quiz
}

export async function listQuizReviewQueue(): Promise<QuizReviewItem[]> {
  const { data, error } = await invokeFunction<{ queue: QuizReviewItem[] }>('training', {
    method: 'GET',
    path: '/quizzes/review-queue',
  })

  if (error) throw toApiRequestError(error, 'Failed to load quiz review queue')
  return ensureArray<QuizReviewItem>(data?.queue)
}

export async function listReviewerQueue(): Promise<TrainingReviewerQueueItem[]> {
  const { data, error } = await invokeFunction<{ queue: TrainingReviewerQueueItem[] }>('training', {
    method: 'GET',
    path: '/review/queue',
  })

  if (error) throw toApiRequestError(error, 'Failed to load reviewer queue')
  return ensureArray<TrainingReviewerQueueItem>(data?.queue).map((item) => ({
    ...item,
    decision_history: ensureArray<TrainingReviewDecision>(item.decision_history),
  }))
}

export async function approveReviewerQueueItem(entityType: 'module' | 'asset' | 'quiz', entityId: string): Promise<TrainingReviewDecision> {
  const { data, error } = await invokeFunction<{ review_decision: TrainingReviewDecision }>('training', {
    method: 'PATCH',
    path: `/review/${entityType}/${entityId}/approve`,
  })

  if (error || !data?.review_decision) throw toApiRequestError(error, 'Failed to approve reviewer item')
  return data.review_decision
}

export async function rejectReviewerQueueItem(entityType: 'module' | 'asset' | 'quiz', entityId: string, reviewComment: string): Promise<TrainingReviewDecision> {
  const { data, error } = await invokeFunction<{ review_decision: TrainingReviewDecision }>('training', {
    method: 'PATCH',
    path: `/review/${entityType}/${entityId}/reject`,
    body: { review_comment: reviewComment },
  })

  if (error || !data?.review_decision) throw toApiRequestError(error, 'Failed to reject reviewer item')
  return data.review_decision
}

export async function approveQuiz(quizId: string): Promise<void> {
  const { error } = await invokeFunction('training', {
    method: 'PATCH',
    path: `/quizzes/${quizId}/approve`,
  })

  if (error) throw toApiRequestError(error, 'Failed to approve quiz')
}

export async function rejectQuiz(quizId: string, reviewComment: string): Promise<void> {
  const { error } = await invokeFunction('training', {
    method: 'PATCH',
    path: `/quizzes/${quizId}/reject`,
    body: { review_comment: reviewComment },
  })

  if (error) throw toApiRequestError(error, 'Failed to reject quiz')
}

export async function listApprovedQuizzes(): Promise<QuizReviewItem[]> {
  const { data, error } = await invokeFunction<{ quizzes: QuizReviewItem[] }>('training', {
    method: 'GET',
    path: '/quizzes',
  })

  if (error) throw toApiRequestError(error, 'Failed to load approved quizzes')
  return ensureArray<QuizReviewItem>(data?.quizzes)
}

export async function getQuizPlayerData(quizId: string): Promise<QuizPlayerData> {
  const { data, error } = await invokeFunction<QuizPlayerData>('training', {
    method: 'GET',
    path: `/quizzes/${quizId}`,
  })

  if (error || !data?.quiz || !Array.isArray(data.questions)) {
    throw toApiRequestError(error, 'Failed to load quiz')
  }

  return {
    quiz: data.quiz,
    questions: data.questions.map((question) => ({
      ...question,
      answer_options: ensureArray<string>(question.answer_options),
    })),
  }
}

export async function submitQuizAttempt(quizId: string, answers: Array<number | null>): Promise<QuizAttemptResult> {
  const { data, error } = await invokeFunction<{ attempt: QuizAttemptResult }>('training', {
    method: 'POST',
    path: `/quizzes/${quizId}/attempt`,
    body: { answers },
  })

  if (error || !data?.attempt) throw toApiRequestError(error, 'Failed to submit quiz attempt')
  return data.attempt
}

export async function listTrainingAssignments(): Promise<TrainingAssignment[]> {
  const { data, error } = await invokeFunction<{ assignments: TrainingAssignment[] }>('training', {
    method: 'GET',
    path: '/assignments',
  })

  if (error) throw toApiRequestError(error, 'Failed to load training assignments')
  return ensureArray<TrainingAssignment>(data?.assignments).map(normalizeTrainingAssignment)
}

export async function createTrainingAssignment(payload: {
  module_id: string
  assignment_scope: 'facility' | 'role' | 'individual'
  due_date: string
  facility_id?: string
  target_role?: string
  assignee_user_id?: string
  source_type?: 'manual' | 'recurring' | 'recommendation' | 'capa' | 'corporate'
  assigned_by_scope?: 'manual' | 'recommendation' | 'corporate' | 'system'
  recommendation_id?: string
  linked_capa_id?: string
}): Promise<TrainingAssignment> {
  const { data, error } = await invokeFunction<{ assignment: TrainingAssignment }>('training', {
    method: 'POST',
    path: '/assignments',
    body: payload,
  })

  if (error || !data?.assignment) throw toApiRequestError(error, 'Failed to create training assignment')
  return normalizeTrainingAssignment(data.assignment)
}

export async function createCompletion(payload: {
  assignment_id: string
  user_id?: string
  attestation_flag?: boolean
}): Promise<void> {
  const { error } = await invokeFunction('training', {
    method: 'POST',
    path: '/completions',
    body: payload,
  })

  if (error) throw toApiRequestError(error, 'Failed to record completion')
}

export async function getTranscript(userId?: string): Promise<CompletionTranscriptRow[]> {
  const path = userId ? `/transcript?user_id=${encodeURIComponent(userId)}` : '/transcript'
  const { data, error } = await invokeFunction<{ transcript: CompletionTranscriptRow[] }>('training', {
    method: 'GET',
    path,
  })

  if (error) throw toApiRequestError(error, 'Failed to load transcript')
  return ensureArray<CompletionTranscriptRow>(data?.transcript)
}

export async function listCompetencyValidations(): Promise<TrainingCompetencyValidation[]> {
  const { data, error } = await invokeFunction<{ competency_validations: TrainingCompetencyValidation[] }>('training', {
    method: 'GET',
    path: '/competencies',
  })

  if (error) throw toApiRequestError(error, 'Failed to load competency validations')
  return ensureArray<TrainingCompetencyValidation>(data?.competency_validations).map((validation) => ({
    ...validation,
    history: ensureArray(validation.history),
    latest_history_entry: validation.latest_history_entry ?? null,
    latest_completion: validation.latest_completion ?? null,
    evidence_package: validation.evidence_package ?? null,
  }))
}

export async function requestCompetencyValidation(payload: {
  assignment_id: string
  validation_notes?: string
}): Promise<TrainingCompetencyValidation> {
  const { data, error } = await invokeFunction<{ competency_validation: TrainingCompetencyValidation }>('training', {
    method: 'POST',
    path: '/competencies/request',
    body: payload,
  })

  if (error || !data?.competency_validation) throw toApiRequestError(error, 'Failed to request competency validation')
  return {
    ...data.competency_validation,
    history: ensureArray(data.competency_validation.history),
    latest_history_entry: data.competency_validation.latest_history_entry ?? null,
    latest_completion: data.competency_validation.latest_completion ?? null,
    evidence_package: data.competency_validation.evidence_package ?? null,
  }
}

export async function reviewCompetencyValidation(payload: {
  competency_validation_id: string
  validation_status: 'approved' | 'rejected'
  validation_notes?: string
}): Promise<TrainingCompetencyValidation> {
  const { data, error } = await invokeFunction<{ competency_validation: TrainingCompetencyValidation }>('training', {
    method: 'PATCH',
    path: `/competencies/${payload.competency_validation_id}/review`,
    body: {
      validation_status: payload.validation_status,
      validation_notes: payload.validation_notes,
    },
  })

  if (error || !data?.competency_validation) throw toApiRequestError(error, 'Failed to review competency validation')
  return {
    ...data.competency_validation,
    history: ensureArray(data.competency_validation.history),
    latest_history_entry: data.competency_validation.latest_history_entry ?? null,
    latest_completion: data.competency_validation.latest_completion ?? null,
    evidence_package: data.competency_validation.evidence_package ?? null,
  }
}

export async function getEvidencePackage(completionId: string): Promise<TrainingEvidencePackage> {
  const { data, error } = await invokeFunction<{ evidence_package: TrainingEvidencePackage }>('training', {
    method: 'GET',
    path: `/evidence-packages/${completionId}`,
  })

  if (error || !data?.evidence_package) throw toApiRequestError(error, 'Failed to load evidence package')
  return data.evidence_package
}

export async function generateEvidencePackage(completionId: string, redacted = true): Promise<TrainingEvidencePackage> {
  const { data, error } = await invokeFunction<{ evidence_package: TrainingEvidencePackage }>('training', {
    method: 'POST',
    path: `/evidence-packages/${completionId}/generate`,
    body: { redacted },
  })

  if (error || !data?.evidence_package) throw toApiRequestError(error, 'Failed to generate evidence package')
  return data.evidence_package
}

export async function requestTrainingExport(redacted: boolean, approvalReason?: string): Promise<{ export_record: { export_id: string; status: string; redacted: boolean } }> {
  const { data, error } = await invokeFunction<{ export_record: { export_id: string; status: string; redacted: boolean } }>('training', {
    method: 'POST',
    path: '/exports',
    body: {
      redacted,
      approval_reason: approvalReason,
    },
  })

  if (error || !data?.export_record) throw toApiRequestError(error, 'Failed to generate export')
  return data
}

export async function recordVideoEvent(moduleId: string, eventType: 'view_started' | 'view_completed'): Promise<void> {
  const { error } = await invokeFunction('training', {
    method: 'POST',
    path: '/analytics/video-events',
    body: { module_id: moduleId, event_type: eventType },
  })

  if (error) throw toApiRequestError(error, 'Failed to record video event')
}

export interface TrainingVideoSummary {
  started: number
  completed: number
  completion_rate_percent: number
  zero_completion_explanation?: string | null
}

export async function getVideoSummary(moduleId?: string): Promise<TrainingVideoSummary> {
  const path = moduleId ? `/analytics/video-summary?module_id=${encodeURIComponent(moduleId)}` : '/analytics/video-summary'
  const { data, error } = await invokeFunction<{ analytics: TrainingVideoSummary }>('training', {
    method: 'GET',
    path,
  })

  if (error || !data?.analytics) throw toApiRequestError(error, 'Failed to load video analytics')
  return data.analytics
}

export async function generateRecommendationDraft(moduleId: string, prompt: string, standardIds: string[]): Promise<RecommendationDraft[]> {
  const { data, error } = await invokeFunction<{ recommendations: RecommendationDraft[] }>('training', {
    method: 'POST',
    path: '/recommendations/generate-draft',
    body: { module_id: moduleId, prompt, standard_ids: standardIds },
  })

  if (error) throw toApiRequestError(error, 'Failed to generate recommendations')
  return ensureArray<RecommendationDraft>(data?.recommendations)
}

export async function listRecommendationDrafts(): Promise<RecommendationDraft[]> {
  const { data, error } = await invokeFunction<{ recommendations: RecommendationDraft[] }>('training', {
    method: 'GET',
    path: '/recommendations/drafts',
  })

  if (error) throw toApiRequestError(error, 'Failed to load recommendation drafts')
  return ensureArray<RecommendationDraft>(data?.recommendations)
}

export async function approveRecommendation(recommendationId: string): Promise<void> {
  const { error } = await invokeFunction('training', {
    method: 'PATCH',
    path: `/recommendations/${recommendationId}/approve`,
  })

  if (error) throw toApiRequestError(error, 'Failed to approve recommendation')
}

export async function linkRecommendation(payload: {
  recommendation_id: string
  remediation_strength?: 'training_only' | 'training_plus_checklist' | 'training_plus_system_change'
  linked_capa_id?: string
  create_assignment?: boolean
  due_date?: string
  assignment_scope?: 'facility' | 'role' | 'individual'
  facility_id?: string
  target_role?: string
  assignee_user_id?: string
  assigned_by_scope?: 'manual' | 'recommendation' | 'corporate' | 'system'
}): Promise<{ recommendation_link: TrainingRecommendationLink; assignment?: TrainingAssignment | null }> {
  const { recommendation_id, ...body } = payload
  const { data, error } = await invokeFunction<{ recommendation_link: TrainingRecommendationLink; assignment?: TrainingAssignment | null }>('training', {
    method: 'POST',
    path: `/recommendations/${recommendation_id}/link`,
    body,
  })

  if (error || !data?.recommendation_link) throw toApiRequestError(error, 'Failed to link recommendation')
  return data
}

export async function getTrainingAdminOverview(): Promise<{ module_count: number; assignment_count: number; completion_count: number; expiring_within_30d_count: number }> {
  const { data, error } = await invokeFunction<{ dashboard: { module_count: number; assignment_count: number; completion_count: number; expiring_within_30d_count: number } }>('training', {
    method: 'GET',
    path: '/admin/overview',
  })

  if (error || !data?.dashboard) throw toApiRequestError(error, 'Failed to load training dashboard')
  return data.dashboard
}
